import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { SparklesIcon } from '@heroicons/react/24/outline';

const RandomGeneratorNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const type = data.config?.type || 'number';
  const min = data.config?.min || 1;
  const max = data.config?.max || 100;

  const getTypeIcon = (t: string) => {
    const icons: Record<string, string> = {
      number: '🔢',
      integer: '#️⃣',
      float: '🔢',
      boolean: '✅',
      string: '📝',
      uuid: '🆔',
      color: '🎨',
      date: '📅',
      email: '📧',
      name: '👤',
      phone: '📞',
      address: '🏠'
    };
    return icons[t] || '🎲';
  };

  const getTypeLabel = (t: string) => {
    const labels: Record<string, string> = {
      number: 'Szám',
      integer: 'Egész szám',
      float: 'Tizedes szám',
      boolean: 'Igaz/Hamis',
      string: 'Szöveg',
      uuid: 'Egyedi azonosító',
      color: 'Szín',
      date: 'Dátum',
      email: 'Email cím',
      name: 'Név',
      phone: 'Telefonszám',
      address: 'Cím'
    };
    return labels[t] || t;
  };

  const getValueRange = () => {
    if (type === 'number' || type === 'integer' || type === 'float') {
      return `${min} - ${max}`;
    }
    if (type === 'string' && data.config?.length) {
      return `${data.config.length} karakter`;
    }
    return '';
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-pink-400 to-pink-500 border-2 transition-all duration-200 ${
      selected ? 'border-pink-600 shadow-xl scale-105' : 'border-pink-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <SparklesIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-pink-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Type and range display */}
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-sm">
              {getTypeIcon(type)}
            </span>
            <span className="text-xs text-pink-100">
              {getTypeLabel(type)}
            </span>
            {getValueRange() && (
              <span className="text-xs text-pink-200 bg-pink-600 bg-opacity-30 px-1 py-0.5 rounded">
                {getValueRange()}
              </span>
            )}
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Generated value preview */}
      {data.executionData && data.executionData.value !== undefined && (
        <div className="mt-2 text-xs text-pink-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <span className="text-lg">{getTypeIcon(type)}</span>
            <span className="font-semibold">Generált érték:</span>
          </div>
          <div className="mt-1 font-mono text-white bg-pink-600 bg-opacity-30 px-2 py-1 rounded">
            {typeof data.executionData.value === 'object' 
              ? JSON.stringify(data.executionData.value)
              : String(data.executionData.value)
            }
          </div>
          {data.executionData.timestamp && (
            <div className="text-xs opacity-75 mt-1">
              {new Date(data.executionData.timestamp).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}

      {/* Configuration preview */}
      {data.config?.seed && (
        <div className="mt-2 text-xs text-pink-200 bg-pink-600 bg-opacity-30 px-2 py-1 rounded">
          <strong>Seed:</strong> {data.config.seed}
        </div>
      )}

      {/* Special config for string type */}
      {type === 'string' && data.config?.pattern && (
        <div className="mt-2 text-xs text-pink-200 bg-pink-600 bg-opacity-30 px-2 py-1 rounded">
          <strong>Minta:</strong> {data.config.pattern}
        </div>
      )}

      {/* No input handle - this is a generator */}
      
      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-pink-600 border-2 border-white"
      />
    </div>
  );
};

export default RandomGeneratorNode;
